import { Box, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import { JSX, useMemo } from 'react';

import { HBIcon } from '../HBIcon/HBIcon';
import { HBNextImage } from '../HBNextImage/HBNextImage';
import { WidgetLinkEnum } from './HBCardWidget/HBCardWidgetList';

export interface HBImageWidgetProps {
  closable?: boolean;
  type?:
    | 'IMAGE_DEFAULT'
    | 'IMAGE-WITHOUT-TITLE'
    | 'ADS_SLIDER_DEFAULT'
    | 'IMAGE_TWO_ROWS'
    | 'IMAGE_ONLY'
    | string;
  sx?: SxProps<Theme>;
  metaData?: string;
  imageUrl?: string;
  title?: string;
  name?: string;
  description?: string;
  noBorderRadius?: boolean;
  objectFit?: NonNullable<JSX.IntrinsicElements['img']['style']>['objectFit'];
}

export const HBImageWidget = (props: HBImageWidgetProps): JSX.Element => {
  const metadata = props?.metaData ? JSON.parse(props.metaData) : {};

  const { type, sx, closable, imageUrl, title, noBorderRadius, objectFit = 'fill', name } = props;

  const calcHeight = useMemo(() => {
    switch (type) {
      case 'IMAGE-WITHOUT-TITLE':
      case 'IMAGE_ONLY':
        return 122;
      case 'IMAGE_DEFAULT':
        return { xs: 126, md: 202 };
      case 'IMAGE_TWO_ROWS':
        return 65;
      case 'ADS_SLIDER_DEFAULT':
        return { xs: 205, md: 243, lg: 380 };
      default:
        return 267;
    }
  }, [type]);

  return (
    <Box
      className={name}
      display={'flex'}
      justifyContent={type === 'ADS_SLIDER_DEFAULT' ? 'start' : 'center'}
      alignItems={type === 'ADS_SLIDER_DEFAULT' ? { xs: 'start', md: 'center' } : 'center'}
      sx={{
        height: calcHeight,
        px: { xs: 2, sm: 'unset' },
        position: 'relative',
        borderRadius: noBorderRadius ? 0 : 4,
        overflow: 'hidden',
        ...sx,
        '&:hover': {
          '& .closeableBox': {
            opacity: 1,
          },
        },
      }}
    >
      {!!imageUrl && (
        <Box
          component={'a'}
          target={metadata.webLinkNavigationType === WidgetLinkEnum.inFrame ? '_self' : '_blank'}
          href={(metadata?.url as string)?.includes('http') ? metadata.url : '//' + metadata.url}
          aria-label={name}
          sx={{
            position: 'absolute',
            cursor: metadata?.url ? 'pointer' : 'auto',
            top: 0,
            right: 0,
            width: '100%',
            height: '100%',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              cursor: metadata?.url ? 'pointer' : 'auto',
              top: 0,
              right: 0,
              width: '100%',
              height: '100%',
            }}
          >
            <HBNextImage
              src={imageUrl || ''}
              width={type === 'ADS_SLIDER_DEFAULT' ? 1920 : undefined}
              quality={95}
              alt={title || ''}
              priority={type === 'ADS_SLIDER_DEFAULT'}
            />
          </Box>
        </Box>
      )}

      {closable && (
        <Box
          className="closeableBox"
          sx={(theme) => ({
            position: 'absolute',
            top: 8,
            right: 8,
            borderRadius: 50,
            bgcolor: 'rgba(255,255,255,0.7)',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            opacity: 0,
            padding: theme.spacing(2),
            '&:hover': {
              backgroundColor: theme.palette.common.white,
            },
          })}
          onClick={(e) =>
            e?.currentTarget?.parentElement?.parentElement?.setAttribute('style', 'display: none')
          }
        >
          <HBIcon type="times" sx={{ color: 'grey.500', fontSize: 16, display: 'flex' }} />
        </Box>
      )}
    </Box>
  );
};

HBImageWidget.defaultProps = { type: 'IMAGE_DEFAULT' };
